import { useEffect, useRef } from 'react';

// Store original title to restore later
const originalTitle = typeof document !== 'undefined' ? document.title : 'Bookings Admin';

// Update tab title with count - simplified for maximum reliability
export const updateTabTitle = (count: number): void => {
  // Skip if we're not in a browser environment
  if (typeof document === 'undefined') {
    return;
  }


  // Update title directly - the most reliable approach
  if (count > 0) {
    document.title = `(${count}) Bookings Admin`;
  } else {
    document.title = 'Bookings Admin';
  }
  
  // Also use window method if available
  if (window.updateTitleWithCount) {
    try {
      window.updateTitleWithCount(count);
    } catch (e) {
      console.error('Error updating title with global method:', e);
    }
  }
};

// Reset title to original
export const resetTitle = (): void => {
  if (typeof document === 'undefined') return;
  document.title = originalTitle;
};

// Show a browser notification
export const showBrowserNotification = (message: string): void => {
  // Check if browser supports notifications and we're in a browser environment
  if (typeof Notification === 'undefined' || typeof window === 'undefined') {
    return;
  }

  // Check if permission is already granted
  if (Notification.permission === 'granted') {
    createNotification(message);
  } 
  // If permission is not denied, request permission
  else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        createNotification(message);
      }
    });
  }
};

// Helper to create the notification
const createNotification = (message: string): void => {
  try {
    const notification = new Notification('Bookings Admin', {
      body: message,
      icon: '/favicon.ico'
    });
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    // Optional: handle click on notification
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

// Play notification sound
export const playNotificationSound = (): void => {
  if (typeof Audio === 'undefined') return;
  
  // Create sound if it doesn't exist
  const sound = new Audio('/notification-sound.mp3');
  sound.volume = 0.5; // 50% volume
  
  // Play the sound
  sound.play().catch(error => {
    // Browser might block autoplay, log error
    console.error('Could not play notification sound:', error);
  });
};

// Hook for tab title notifications
export const useTabNotifications = (
  count: number,
  playSound: boolean = false,
  previousCountRef?: React.RefObject<number>
): void => {
  const internalPrevCountRef = useRef<number>(0);
  const prevCount = previousCountRef?.current ?? internalPrevCountRef.current;
  
  useEffect(() => {
    
    // Always update the tab title
    updateTabTitle(count);
    
    // Show browser notification and play sound if count increased
    if (count > prevCount) {
      // Only show notification if count actually increased (not on first load)
      if (prevCount > 0) {
        showBrowserNotification(`You have ${count} pending booking${count !== 1 ? 's' : ''} that require approval.`);
      }
      
      // Play sound if enabled
      if (playSound) {
        playNotificationSound();
      }
    }
    
    // Store current count for next comparison
    internalPrevCountRef.current = count;
    
    // Cleanup when component unmounts
    return () => {
      resetTitle();
    };
  }, [count, playSound, prevCount]);
  
  // Force an update when the component mounts
  useEffect(() => {
    // Initial update of the title
    updateTabTitle(count);
    
    // Request notification permission on component mount
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      // Just check if we can request permission without actually showing a notification
      Notification.requestPermission();
    }
    
    // Cleanup when unmounting
    return () => {
      resetTitle();
    };
  }, [count]);
}; 